import {newClientKey} from 'app/pages/school/StudentComments/utils'
import {calculateErrorMessage} from 'common/helpers'

import {AttendanceChatMessage, ConversationHistoryItem} from '../attendance/attendanceWizardTypes'

/** Kept as-is for the general conversation, so sessions saved before surfaces existed restore. */
export const ASSISTANT_SESSION_KEY = 'assistant-chatbot-session'

/** The surfaces that keep a conversation of their own. */
export type AssistantSessionSurface = 'general' | 'admission_admin' | 'admission_applicant'

const SESSION_SURFACES: AssistantSessionSurface[] = [
  'general',
  'admission_admin',
  'admission_applicant',
]

/** Keys the two pre-merge bots persisted under; wiped whenever the unified session is. */
const LEGACY_SESSION_KEYS = ['attendance-chatbot-session', 'faq-chatbot-session']

export type AssistantChatSession = {
  userId: string
  messages: AttendanceChatMessage[]
  conversationHistory: ConversationHistoryItem[]
  savedAt: string
}

/**
 * Who a stored conversation belongs to when nobody is signed in. Only the applicant surface is
 * ever reachable signed out (the public `/admissions/*` pages), so only its conversation uses it.
 */
export const ANONYMOUS_SESSION_OWNER = 'anonymous'

export function assistantSessionOwner(userId: string | undefined): string {
  return userId || ANONYMOUS_SESSION_OWNER
}

/**
 * The applicant conversation belongs to whoever was on the page when it was had. When the owner
 * changes — an anonymous visitor signs in, one user is replaced by another — the stored one is
 * dropped instead of being handed to the new identity. On the first run (`previousOwner` null)
 * only a conversation stored for someone else is dropped. Returns whether anything was cleared,
 * so the caller can empty the conversation it holds in state too.
 */
export function releaseApplicantSessionOnOwnerChange(
  previousOwner: string | null,
  nextOwner: string
): boolean {
  if (previousOwner !== null && previousOwner !== nextOwner) {
    clearAssistantSession('admission_applicant')
    return true
  }
  if (previousOwner === null) {
    const stored = peekAssistantSession('admission_applicant')
    if (stored && stored.userId !== nextOwner) {
      clearAssistantSession('admission_applicant')
      return true
    }
  }
  return false
}

export function sessionKeyFor(surface: AssistantSessionSurface = 'general'): string {
  return surface === 'general' ? ASSISTANT_SESSION_KEY : `${ASSISTANT_SESSION_KEY}:${surface}`
}

function conversationIdKeyFor(surface: AssistantSessionSurface): string {
  return `${sessionKeyFor(surface)}:conversation-id`
}

/** Last resort when sessionStorage is unavailable, so one chat still keeps one id. */
const conversationIdFallback: Partial<Record<AssistantSessionSurface, string>> = {}

/**
 * The id sent as `options.conversationId` on every turn of the conversation on screen. The
 * server keys its chat session on it, so two chats open under one login (two tabs, QA running
 * prompts side by side) no longer share a class or homework (HW-14 / HW-25). Kept per tab in
 * sessionStorage, so a reload continues the same conversation; a new one is minted once the
 * conversation is cleared ("New chat", logout, another user).
 */
export function getAssistantConversationId(surface: AssistantSessionSurface = 'general'): string {
  const key = conversationIdKeyFor(surface)
  try {
    const stored = sessionStorage.getItem(key)
    if (stored) return stored
    const id = newClientKey()
    sessionStorage.setItem(key, id)
    return id
  } catch (e) {
    calculateErrorMessage(e)
    const id = conversationIdFallback[surface] ?? newClientKey()
    conversationIdFallback[surface] = id
    return id
  }
}

/** Clears one surface's conversation — "New chat" starts over only the one on screen. */
export function clearAssistantSession(surface: AssistantSessionSurface = 'general'): void {
  delete conversationIdFallback[surface]
  try {
    sessionStorage.removeItem(sessionKeyFor(surface))
    sessionStorage.removeItem(conversationIdKeyFor(surface))
    if (surface === 'general') {
      LEGACY_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key))
    }
  } catch (e) {
    calculateErrorMessage(e)
  }
}

/** Clears every surface's conversation — on logout or when the stored user is someone else. */
export function clearAllAssistantSessions(): void {
  SESSION_SURFACES.forEach((surface) => clearAssistantSession(surface))
}

function parseStoredMessages(messages: AttendanceChatMessage[]): AttendanceChatMessage[] {
  return messages.map((m) => ({
    ...m,
    timestamp:
      m.timestamp instanceof Date
        ? m.timestamp
        : m.timestamp
          ? new Date(m.timestamp as unknown as string)
          : new Date(),
  }))
}

function parseStoredSession(raw: string): AssistantChatSession | null {
  const parsed = JSON.parse(raw) as AssistantChatSession
  if (!parsed || !Array.isArray(parsed.messages)) return null

  return {
    userId: parsed.userId,
    messages: parseStoredMessages(parsed.messages),
    conversationHistory: Array.isArray(parsed.conversationHistory)
      ? parsed.conversationHistory
      : [],
    savedAt: parsed.savedAt ?? new Date().toISOString(),
  }
}

/** Read raw session before auth is ready — used for synchronous state init on page load. */
export function peekAssistantSession(
  surface: AssistantSessionSurface = 'general'
): AssistantChatSession | null {
  try {
    if (sessionStorage.getItem('logout') === 'true') return null

    const raw = sessionStorage.getItem(sessionKeyFor(surface))
    if (!raw) return null

    const session = parseStoredSession(raw)
    return session && session.messages.length > 0 ? session : null
  } catch (e) {
    calculateErrorMessage(e)
    return null
  }
}

export function loadAssistantSession(
  userId: string | undefined,
  surface: AssistantSessionSurface = 'general'
): AssistantChatSession | null {
  if (!userId) return null

  try {
    if (sessionStorage.getItem('logout') === 'true') {
      clearAllAssistantSessions()
      return null
    }

    const raw = sessionStorage.getItem(sessionKeyFor(surface))
    if (!raw) return null

    const session = parseStoredSession(raw)
    if (!session || session.userId !== userId) {
      clearAllAssistantSessions()
      return null
    }

    return session
  } catch (e) {
    calculateErrorMessage(e)
    clearAssistantSession(surface)
    return null
  }
}

export function saveAssistantSession(
  session: Omit<AssistantChatSession, 'savedAt'>,
  surface: AssistantSessionSurface = 'general'
): void {
  try {
    if (!session.userId || session.messages.length === 0) {
      clearAssistantSession(surface)
      return
    }

    const payload = {
      userId: session.userId,
      messages: session.messages.map((m) => ({
        ...m,
        timestamp:
          m.timestamp instanceof Date
            ? m.timestamp.toISOString()
            : m.timestamp
              ? String(m.timestamp)
              : new Date().toISOString(),
      })),
      conversationHistory: session.conversationHistory,
      savedAt: new Date().toISOString(),
    }

    sessionStorage.setItem(sessionKeyFor(surface), JSON.stringify(payload))
  } catch (e) {
    calculateErrorMessage(e)
  }
}
